// Client-side counterpart to utils/katexRender.js. Renders $...$ / $$...$$ LaTeX
// delimiters to KaTeX markup – used both for AI-generated slide fields (buildHtml)
// and for edit-mode toggling in contenteditable slide text: a field shows raw
// LaTeX source while focused (so editing is just normal text editing, no atomic
// non-editable spans to navigate around) and renders to KaTeX as soon as it's
// no longer selected (see installLiveEditing).
const MathRender = (() => {
    const MATH_REGEX = /\$\$([^$]+?)\$\$|\$([^$\n]+?)\$/g;

    function escapeHtml(str) {
        if (typeof str !== 'string') return '';
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // A fill-in-the-blank gap written inside a formula ("\frac{_____}{15}")
    // is not LaTeX - KaTeX prints the raw source in red. Drawn as an empty
    // answer box instead; data-latex keeps the author's underscores.
    const texGaps = tex => tex.replace(/(?:\\?_){3,}/g, '{\\boxed{\\phantom{00}}}');

    function katexHtmlFor(tex, displayMode) {
        try {
            return window.katex.renderToString(texGaps(tex), { throwOnError: false, displayMode });
        } catch {
            return escapeHtml(tex);
        }
    }

    // The LaTeX inside already-HTML text is entity-escaped like the rest of it:
    // "x > 0" is stored as "x &gt; 0" and a matrix's column separator as "&amp;".
    // KaTeX needs the characters themselves – handed the entity it reads the "&"
    // as a stray alignment tab and the whole formula fails to render.
    function decodeTexEntities(tex) {
        return tex
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
    }

    // String-based render (same contract as the server helper) – used by
    // slideRenderer.js in place of escapeHtml() for slide text fields, and to
    // sanitize a raw mid-edit HTML snapshot right before it's persisted (see
    // renderMathHtml below).
    function renderTextWithMath(str) {
        if (typeof str !== 'string' || !str) return '';
        let out = '';
        let lastIndex = 0;
        let match;
        MATH_REGEX.lastIndex = 0;
        while ((match = MATH_REGEX.exec(str)) !== null) {
            out += escapeHtml(str.slice(lastIndex, match.index));
            const isBlock = match[1] !== undefined;
            const tex = isBlock ? match[1] : match[2];
            out += `<span class="${isBlock ? 'katex-display-wrap' : 'katex-inline'}" contenteditable="false" data-latex="${escapeHtml(tex)}">${katexHtmlFor(tex, isBlock)}</span>`;
            lastIndex = MATH_REGEX.lastIndex;
        }
        out += escapeHtml(str.slice(lastIndex));
        return out;
    }

    // Same delimiter scan as renderTextWithMath, but for a string that's already
    // HTML – only the matched $...$/$$...$$ runs are replaced, everything else
    // (tags, entities, surrounding text) passes through untouched. Mirrors
    // utils/katexRender.js::renderMathHtml exactly.
    function renderMathHtml(html) {
        if (typeof html !== 'string' || !html) return html;
        return html.replace(/(<[^>]*>)|([^<]+)/g, (full, tag, text) => {
            if (tag) return tag;
            return text.replace(MATH_REGEX, (m, block, inline) => {
                const isBlock = block !== undefined;
                const tex = decodeTexEntities(isBlock ? block : inline);
                return `<span class="${isBlock ? 'katex-display-wrap' : 'katex-inline'}" contenteditable="false" data-latex="${escapeHtml(tex)}">${katexHtmlFor(tex, isBlock)}</span>`;
            });
        });
    }

    // Rich content: like renderMathHtml but with the renderer's non-string guard.
    function renderRichText(html) {
        if (typeof html !== 'string' || !html) return '';
        return renderMathHtml(html);
    }

    // `doc` rather than the ambient `document`: the worksheet editor renders
    // the sheet inside a same-origin iframe and toggles math in there, so
    // every node this file creates has to belong to the document it is going
    // into. Slides pass their own document and are unaffected.
    function makeMathSpan(doc, tex, displayMode) {
        const span = doc.createElement('span');
        span.className = displayMode ? 'katex-display-wrap' : 'katex-inline';
        span.contentEditable = 'false';
        span.dataset.latex = tex;
        span.innerHTML = katexHtmlFor(tex, displayMode);
        return span;
    }

    // Splits one text node in place wherever it contains a complete $...$/$$...$$
    // match, inserting rendered (non-editable) math spans.
    function scanAndReplaceTextNode(textNode) {
        const doc = textNode.ownerDocument || document;
        const text = textNode.nodeValue;
        MATH_REGEX.lastIndex = 0;
        let match;
        let lastIndex = 0;
        let found = false;
        const frag = doc.createDocumentFragment();
        while ((match = MATH_REGEX.exec(text)) !== null) {
            found = true;
            if (match.index > lastIndex) frag.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
            const isBlock = match[1] !== undefined;
            frag.appendChild(makeMathSpan(doc, isBlock ? match[1] : match[2], isBlock));
            lastIndex = MATH_REGEX.lastIndex;
        }
        if (!found) return false;
        if (lastIndex < text.length) frag.appendChild(doc.createTextNode(text.slice(lastIndex)));
        textNode.parentNode.replaceChild(frag, textNode);
        return true;
    }

    function textNodesToScan(el) {
        const walker = (el.ownerDocument || document).createTreeWalker(el, NodeFilter.SHOW_TEXT, {
            acceptNode(node) {
                return node.parentElement && node.parentElement.closest('.katex-inline, .katex-display-wrap')
                    ? NodeFilter.FILTER_REJECT
                    : NodeFilter.FILTER_ACCEPT;
            },
        });
        const nodes = [];
        let n;
        while ((n = walker.nextNode())) nodes.push(n);
        return nodes;
    }

    // Scans el's text for complete $...$/$$...$$ expressions and renders them in
    // place. Used on blur (field is no longer being edited) and on initial slide
    // load to catch any raw math that reaches the client unrendered.
    function renderMathInElement(el) {
        let changed = false;
        for (const node of textNodesToScan(el)) {
            if (scanAndReplaceTextNode(node)) changed = true;
        }
        return changed;
    }

    // Reverts every rendered math span in el back to raw $...$/$$...$$ source
    // text. Used on focus so editing a field is just normal text editing.
    function revertMathInElement(el) {
        const spans = el.querySelectorAll('.katex-inline, .katex-display-wrap');
        spans.forEach((span) => {
            const tex = span.dataset.latex || '';
            const delim = span.classList.contains('katex-display-wrap') ? '$$' : '$';
            span.parentNode.replaceChild((span.ownerDocument || document).createTextNode(delim + tex + delim), span);
        });
        return spans.length > 0;
    }

    // Wires the focus/blur edit-mode toggle via event delegation on a stable
    // ancestor container – call once at setup, not per contenteditable element
    // (elements come and go as selection changes). focusin/focusout bubble
    // (unlike focus/blur), so a single pair of listeners here covers every
    // contenteditable field inside the container.
    function installLiveEditing(container) {
        container.addEventListener('focusin', (e) => {
            if (e.target && e.target.isContentEditable) revertMathInElement(e.target);
        });
        container.addEventListener('focusout', (e) => {
            if (e.target && e.target.isContentEditable) renderMathInElement(e.target);
        });
    }

    return { renderTextWithMath, renderMathHtml, renderRichText, renderMathInElement, revertMathInElement, installLiveEditing };
})();
